/** Notice.tsx — inline message block. `danger` keeps the focal `ring-2 ring-status-down`. */

import type { HTMLAttributes, ReactNode } from 'react';

export type NoticeTone = 'danger' | 'warning' | 'info';

const TONE_CLASSES: Record<NoticeTone, { box: string; text: string }> = {
  danger: { box: 'bg-status-down/10 ring-2 ring-status-down', text: 'text-status-down' },
  warning: {
    box: 'bg-status-degraded/10 ring-1 ring-status-degraded/40',
    text: 'text-status-degraded',
  },
  info: { box: 'bg-surface-2 ring-1 ring-ink-3/20', text: 'text-ink-1' },
};

export interface NoticeProps extends Omit<HTMLAttributes<HTMLDivElement>, 'children'> {
  tone: NoticeTone;
  children: ReactNode;
  action?: ReactNode;
}

export const Notice = ({
  tone,
  children,
  action = undefined,
  className = '',
  ...rest
}: NoticeProps) => (
  <div
    // eslint-disable-next-line react/jsx-props-no-spreading -- primitive forwards native HTML attributes (data-testid, aria-*) to the root element
    {...rest}
    className={`flex flex-col items-start gap-3 rounded-xl p-4 ring-inset ${TONE_CLASSES[tone].box} ${className}`.trim()}
  >
    <p
      role={tone === 'info' ? 'status' : 'alert'}
      className={`text-body ${TONE_CLASSES[tone].text}`}
    >
      {children}
    </p>
    {action}
  </div>
);
